import { Printer } from 'lucide-react';
import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button } from '@/components/ui';
import {
  DOCUMENT_LABELS,
  type ClinicalDocument,
} from '@/features/encounters/api';
import { IMAGING_STATUS_LABEL } from '@/features/orders/types';
import { verifyUrl } from './consult-utils';
import styles from './consult.module.css';
import { canPrintTimeSlip, type TimeSlipContext } from './time-slip';
import { TimeSlipPrintSheet } from './TimeSlipPrintSheet';

function Qr({ code }: { code: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    QRCode.toDataURL(verifyUrl(code), { margin: 1, width: 72 })
      .then((d) => live && setSrc(d))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [code]);
  return src ? (
    <img src={src} width={72} height={72} alt="QR check code" />
  ) : (
    <span className={styles.qrBlank} aria-hidden="true" />
  );
}

/**
 * The document's details, or null when the API sends none: RL-1667 (gap #77)
 * roles without clinical access (front desk) get `data: {}`, so they see the
 * document type, number and date only, never "From undefined".
 */
function summary(d: ClinicalDocument): string | null {
  const x = d.data;
  if (!x || Object.keys(x).length === 0) return null;
  switch (d.kind) {
    case 'medical_certificate':
      return `${String(x.days)} day(s) unfit, ${String(x.from)} to ${String(x.to)}`;
    case 'time_slip':
      return `${x.for === 'companion' ? `Companion ${String(x.companionName ?? '')}` : 'Patient'} · ${String(x.arrived)} to ${String(x.leaving)}`;
    case 'referral_letter':
      return `${String(x.specialty)} · ${String(x.hospital)}`;
    case 'imaging_referral':
      return `${String(x.exam)} · ${String(x.where)}`;
    case 'vaccination_certificate':
      return `${String(x.vaccine)} · dose ${String(x.dose)} · batch ${x.batch ? String(x.batch) : 'not recorded'}`;
    case 'outside_prescription':
      return `From ${String(x.prescriptionNumber)}`;
  }
}

/**
 * Documents issued from the consultation, each with its QR check. `slipContext` names the patient and
 * branch a time slip belongs to (the document does not carry them): without it, or for a revoked slip or
 * a role that is sent no details, there is no Print button.
 */
export function DocumentList({
  documents,
  slipContext,
}: {
  documents: ClinicalDocument[];
  slipContext?: (doc: ClinicalDocument) => TimeSlipContext | null;
}) {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState<ClinicalDocument | null>(null);
  const donePrinting = useCallback(() => setPrinting(null), []);
  if (!documents.length) return null;
  const printContext = printing && slipContext ? slipContext(printing) : null;
  return (
    <>
      <ul className={styles.docList} aria-label="Documents issued">
        {documents.map((d) => (
          <li key={d.id}>
            <Qr code={d.verificationCode} />
            <div>
              <strong>{DOCUMENT_LABELS[d.kind]}</strong>{' '}
              <span className={styles.mono}>{d.number}</span>
              {d.revokedAt && <Badge tone="danger">Revoked</Badge>}
              {d.imaging && (
                <Badge
                  tone={d.imaging.status === 'report_in' ? 'warning' : 'info'}
                >
                  {IMAGING_STATUS_LABEL[d.imaging.status]}
                </Badge>
              )}
              {summary(d) && (
                <>
                  <br />
                  <span className={styles.sub}>{summary(d)}</span>
                </>
              )}
              {d.imaging?.reportText && (
                <>
                  <br />
                  <span className={styles.sub}>
                    Report: {d.imaging.reportText}
                    {d.imaging.reviewComment
                      ? ` · doctor: ${d.imaging.reviewComment}`
                      : ''}
                  </span>
                </>
              )}
              <br />
              <span className={styles.sub}>
                Issued {new Date(d.issuedAt).toLocaleString('en-GB')}
                {d.issuedBy ? ` · ${d.issuedBy.fullName}` : ''}
              </span>
              {canPrintTimeSlip(d) && slipContext?.(d) && (
                <>
                  <br />
                  <Button
                    size="sm"
                    icon={<Printer size={14} />}
                    aria-label={t('timeSlip.printLabel', { number: d.number })}
                    onClick={() => setPrinting(d)}
                  >
                    {t('timeSlip.print')}
                  </Button>
                </>
              )}
            </div>
          </li>
        ))}
      </ul>
      {printing && printContext && canPrintTimeSlip(printing) && (
        <TimeSlipPrintSheet
          document={printing}
          context={printContext}
          onDone={donePrinting}
        />
      )}
    </>
  );
}
